import React, { useEffect, useState } from "react";
import {
  AccordionContainer,
  AccordionHeader,
  AccordionContent,
  AccordionDetailContainer,
  ChevronIcon,
  AccordionLine,
  AccordionHeaderContent,
} from "./Accordion.styles";
import { AccordionProps } from "./Accordion.types";
import {
  Body5MediumTypography,
  Body5RegularTypography,
  Heading5SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";

export const Accordion: React.FC<AccordionProps> = ({
  question,
  subtitle,
  answer,
  accordionDetail,
  disabled = false,
  defaultOpen = false,
  backgroundColor,
  bottomLineColor,
  onAccordionOpen,
  variant = "normal",
  marginBottom = "8px",
  isOpen: controlledIsOpen,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  // Determine if we're in controlled mode
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  useEffect(() => {
    if (!isControlled) {
      setInternalIsOpen(defaultOpen);
    }
  }, [defaultOpen, isControlled]);

  const toggleAccordion = () => {
    if (!disabled) {
      const willOpen = !isOpen;

      // Update state based on controlled/uncontrolled mode
      if (!isControlled) {
        setInternalIsOpen(willOpen);
      }

      // Call onAccordionOpen with new state
      // For controlled mode: parent handles state via isOpen prop
      // For uncontrolled mode: component handles state internally
      if (onAccordionOpen) {
        onAccordionOpen(willOpen);
      }
    }
  };

  const isBigVariant = variant === "big";

  return (
    <AccordionContainer
      isOpen={isOpen}
      disabled={disabled}
      backgroundColor={backgroundColor}
      variant={variant}
      marginBottom={marginBottom}
      bottomLineColor={bottomLineColor}
    >
      <AccordionHeader onClick={toggleAccordion} variant={variant}>
        {isBigVariant ? (
          <AccordionHeaderContent>
            <Heading5SemiboldTypography color="text.default.default">
              {question}
            </Heading5SemiboldTypography>
            {subtitle && (
              <Body5RegularTypography color="text.default.default">
                {subtitle}
              </Body5RegularTypography>
            )}
          </AccordionHeaderContent>
        ) : (
          <Body5MediumTypography>{question}</Body5MediumTypography>
        )}
        <ChevronIcon
          isOpen={isOpen}
          variant={variant}
          className="fa-solid fa-chevron-down"
        />
      </AccordionHeader>

      {isOpen && !disabled && (
        <AccordionContent>
          {answer && <Body5RegularTypography>{answer}</Body5RegularTypography>}
          {accordionDetail && (
            <AccordionDetailContainer>
              {accordionDetail}
            </AccordionDetailContainer>
          )}
        </AccordionContent>
      )}
      {!isBigVariant && <AccordionLine lineColor={bottomLineColor} />}
    </AccordionContainer>
  );
};
